import { useEffect, useId, useRef } from 'react';

import { createPortal } from 'react-dom';

import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { AnimatePresence } from 'motion/react';
import PropTypes from 'prop-types';

import OverlayFadeMotion from 'components/Motion/OverlayFadeMotion';
import SliderSideMotion from 'components/Motion/SliderSideMotion';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const Drawer = ({
  open,
  onClose = () => {},
  onExited,
  disableClose = false,
  title,
  description,
  children,
  dataTestId,
}) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const previousActiveElement = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Lock background scroll and handle Escape key (depend only on open so focus isn't lost when onClose ref changes)
  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement;
      const prevHtml = document.documentElement.style.overflow;
      const prevBody = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      const handleKeyDown = e => {
        if (e.key === 'Escape') {
          if (!disableClose) onCloseRef.current();
          return;
        }
        if (
          e.key !== 'Tab' ||
          !panelRef.current ||
          !panelRef.current.contains(document.activeElement)
        )
          return;
        const focusable = panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.documentElement.style.overflow = prevHtml;
        document.body.style.overflow = prevBody;
        previousActiveElement.current?.focus?.();
      };
    }
  }, [open, disableClose]);

  // Move focus into drawer when it opens
  useEffect(() => {
    if (open && panelRef.current) {
      const focusable = panelRef.current.querySelector(FOCUSABLE_SELECTOR);
      (focusable || panelRef.current).focus({ preventScroll: true });
    }
  }, [open]);

  const drawerContent = (
    <AnimatePresence onExitComplete={onExited}>
      {open && (
        <>
          <OverlayFadeMotion
            keyProp="drawer-backdrop"
            className="fixed inset-0 bg-black/30 z-[9998]"
            duration={0.2}
            onClick={() => {
              if (!disableClose) onCloseRef.current();
            }}
          />

          <SliderSideMotion
            keyProp="drawer-panel"
            ref={panelRef}
            xAxis="100%"
            opacity={1}
            turnOffOpacityTransition
            exitSlideOnly
            className="fixed top-0 right-0 h-full w-[500px] max-w-full bg-white z-[9999] flex flex-col shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] outline-none"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            data-testid={dataTestId}>
            {/* Header: title/description + close icon button at top right (hidden when disableClose) */}
            <div className="flex items-start justify-between px-10 py-6 border-b border-border-primary shrink-0">
              <div className="flex flex-col gap-1">
                <h2 id={titleId} className="text-text-primary text-4xl font-semibold">
                  {title}
                </h2>
                {description && (
                  <p className="m-0 text-text-secondary text-xl font-regular">{description}</p>
                )}
              </div>
              {!disableClose && (
                <button
                  type="button"
                  onClick={() => onCloseRef.current()}
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-border-primary text-text-secondary hover:bg-neutral-100 hover:border-neutral-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-6 shrink-0"
                  aria-label="Close">
                  <HugeiconsIcon icon={Cancel01Icon} size={22} strokeWidth={1.5} />
                </button>
              )}
            </div>

            {/* Body scrolls in row 1; footer stays in row 2 (pinned to bottom of panel) */}
            <div className="grid flex-1 min-h-0 grid-rows-[minmax(0,1fr)_auto] overflow-hidden">
              {children}
            </div>
          </SliderSideMotion>
        </>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(drawerContent, document.body) : null;
};

const DrawerBody = ({ children, className = '' }) => (
  <div className={`min-h-0 overflow-y-auto px-10 pt-10 pb-10 ${className}`}>{children}</div>
);

const DrawerFooter = ({ children, className = '' }) => (
  <div
    className={`relative z-10 shrink-0 border-t border-[#E5E5E5] bg-white px-10 py-[25px] ${className}`}>
    {children}
  </div>
);

Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onExited: PropTypes.func,
  disableClose: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node,
  dataTestId: PropTypes.string,
};

DrawerBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

DrawerFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};
